// /crisis — Modo Crisis: qué está vigilando el sistema y qué encontró.
//
// Dos mitades:
//   · Situaciones — lo que el radar detectó, abierto y cerrado.
//   · Temas vigilados — las palabras que hacen que el sistema se despierte.
//
// La segunda es la que más rinde: cuanto más específicas las palabras (razones
// sociales, nombres de locales, direcciones), menos falsas alarmas y menos
// chances de que algo se escape.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaFireAlt, FaPlus, FaTrash, FaEye, FaEyeSlash, FaFolderOpen, FaCheck,
} from 'react-icons/fa';
import {
  TechShell, TechHeader, TechTitle, TechSubtitle, TechPanel, PanelHeader, PanelTitle,
  TechInput, TechTextarea, TechSelect, TechButton, TechButtonGhost, TechButtonDanger,
  TechChip, Empty, ErrorBox, Loading, mono, borderSoft,
} from '../../styles/techComponents';
import {
  getCrisis, cambiarEstadoCrisis, getTemasVigilados, crearTemaVigilado,
  actualizarTemaVigilado, borrarTemaVigilado,
} from '../../services/crisisService';
import { useCrisis } from '../../contexts/CrisisContext';
import friendlyError from '../../utils/friendlyError';

const Fila = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${borderSoft};
  flex-wrap: wrap;
  &:last-child { border-bottom: none; }
`;

const Principal = styled.div`
  flex: 1;
  min-width: 220px;
`;

const Nombre = styled.div`
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--text-primary);
  margin-bottom: 3px;
`;

const Detalle = styled.div`
  font-size: 0.82rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Palabras = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
`;

// Las anclas se destacan: son las que definen de qué caso se trata.
const Palabra = styled.span`
  font-family: ${mono};
  font-size: 0.68rem;
  color: ${(p) => (p.$ancla ? 'var(--btn-primary)' : 'var(--text-secondary)')};
  background: ${(p) => (p.$ancla ? 'rgba(0, 180, 216, 0.1)' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid ${(p) => (p.$ancla ? 'rgba(0, 180, 216, 0.35)' : borderSoft)};
  padding: 2px 6px;
  border-radius: 3px;
`;

const Sub = styled.div`
  font-size: 0.74rem; color: var(--alert-warning); margin-top: 6px;
`;

const Botones = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
`;

const Formulario = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  margin-top: 12px;
  border: 1px dashed ${borderSoft};
  border-radius: 8px;
`;

const Doble = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  > * { flex: 1; min-width: 160px; }
`;

const Ayuda = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const tonoPorSeveridad = (s) => (s === 'alta' ? 'danger' : s === 'media' ? 'warn' : undefined);

const ETIQUETA_ESTADO = {
  detectada: 'sin confirmar',
  confirmada: 'en curso',
  contenida: 'contenida',
  resuelta: 'resuelta',
  falsa_alarma: 'falsa alarma',
};

const VACIO = { tema: '', descripcion: '', keywords: '', anclas: '', severidad_base: 'alta' };

export default function ModoCrisis() {
  const { refrescar: refrescarBanner } = useCrisis();

  const [datos, setDatos] = useState(null);
  const [temas, setTemas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState(VACIO);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const [c, t] = await Promise.all([getCrisis(), getTemasVigilados()]);
      setDatos(c);
      setTemas(t?.temas || []);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (id, estado) => {
    setOcupado(true);
    try {
      await cambiarEstadoCrisis(id, estado);
      await Promise.all([cargar(), refrescarBanner()]);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setOcupado(false);
    }
  };

  const guardarNuevo = async () => {
    if (nuevo.tema.trim().length < 4) { setError('Poné un nombre al tema.'); return; }
    setOcupado(true);
    try {
      await crearTemaVigilado({
        tema: nuevo.tema.trim(),
        descripcion: nuevo.descripcion.trim() || null,
        keywords: nuevo.keywords,
        anclas: nuevo.anclas,
        severidad_base: nuevo.severidad_base,
      });
      setNuevo(VACIO);
      setCreando(false);
      await cargar();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setOcupado(false);
    }
  };

  const alternarTema = async (t) => {
    setOcupado(true);
    try {
      await actualizarTemaVigilado(t.id, { activo: !t.activo });
      await cargar();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setOcupado(false);
    }
  };

  const eliminarTema = async (t) => {
    setOcupado(true);
    try {
      await borrarTemaVigilado(t.id);
      await cargar();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setOcupado(false);
    }
  };

  if (cargando) return <Loading>Cargando el estado de las situaciones…</Loading>;

  const abiertas = datos?.abiertas || [];
  const oportunidades = datos?.oportunidades || [];
  const historial = datos?.historial || [];
  const vigilando = temas.filter((t) => t.activo).length;

  // Fila de una situación (crisis u oportunidad), reutilizable.
  const filaSituacion = (c, esOp) => (
    <Fila key={c.id}>
      <Principal>
        <Nombre>{c.titulo}</Nombre>
        <Detalle>
          {c.senales} {c.senales === 1 ? 'señal' : 'señales'} · desde el{' '}
          {new Date(c.first_seen).toLocaleDateString('es-AR')} · última el{' '}
          {new Date(c.last_seen).toLocaleDateString('es-AR')}
        </Detalle>
      </Principal>
      <TechChip tone={esOp ? undefined : tonoPorSeveridad(c.severidad)}>
        {c.severidad} · {ETIQUETA_ESTADO[c.estado] || c.estado}
      </TechChip>
      <Botones>
        <Link to={`/crisis/${c.id}`}>
          <TechButton as="span"><FaFolderOpen /> {esOp ? 'Abrir el kit de ataque' : 'Abrir sala de situación'}</TechButton>
        </Link>
        {c.estado === 'detectada' && (
          <TechButton onClick={() => cambiarEstado(c.id, 'confirmada')} disabled={ocupado}>
            <FaCheck /> {esOp ? 'Tomarla' : 'Confirmar'}
          </TechButton>
        )}
        <TechButtonGhost onClick={() => cambiarEstado(c.id, 'resuelta')} disabled={ocupado}>
          {esOp ? 'Ya la usé' : 'Dar por cerrada'}
        </TechButtonGhost>
        {c.estado === 'detectada' && (
          <TechButtonDanger onClick={() => cambiarEstado(c.id, 'falsa_alarma')} disabled={ocupado}>
            {esOp ? 'Descartar' : 'No era nada'}
          </TechButtonDanger>
        )}
      </Botones>
    </Fila>
  );

  return (
    <TechShell>
      <TechHeader>
        <TechTitle><FaFireAlt /> Modo Crisis</TechTitle>
        <TechSubtitle>
          El sistema mira solo la prensa, las noticias de los rivales y los reclamos de los
          vecinos. Si aparece algo de los temas que vigila, abre la situación, arma la respuesta
          y te avisa al teléfono.
        </TechSubtitle>
      </TechHeader>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* ---------- Situaciones abiertas ---------- */}
      <TechPanel>
        <PanelHeader>
          <PanelTitle>Situaciones abiertas</PanelTitle>
        </PanelHeader>

        {abiertas.length === 0 ? (
          <Empty>
            Ninguna situación abierta. El sistema está vigilando {vigilando}{' '}
            {vigilando === 1 ? 'tema' : 'temas'}.
          </Empty>
        ) : (
          abiertas.map((c) => filaSituacion(c, false))
        )}
      </TechPanel>

      {/* ---------- Oportunidades para atacar (ofensivo) ---------- */}
      {oportunidades.length > 0 && (
        <TechPanel style={{ borderColor: 'rgba(6,214,160,0.35)' }}>
          <PanelHeader>
            <PanelTitle style={{ color: '#06d6a0' }}>🎯 Oportunidades para atacar</PanelTitle>
            <TechChip>lo que hizo mal el rival</TechChip>
          </PanelHeader>
          {oportunidades.map((c) => filaSituacion(c, true))}
        </TechPanel>
      )}

      {/* ---------- Temas vigilados ---------- */}
      <TechPanel>
        <PanelHeader>
          <PanelTitle>Qué está vigilando</PanelTitle>
          <TechButton onClick={() => setCreando((v) => !v)} disabled={ocupado}>
            <FaPlus /> Agregar tema
          </TechButton>
        </PanelHeader>

        <Ayuda>
          Cada tema tiene <strong>nombres propios</strong> (la persona, las empresas) y{' '}
          <strong>palabras del tema</strong> (clausura, multa, inspección). Una nota cuenta solo si
          menciona alguno de los nombres propios <em>y</em> alguna de las palabras. Sin los nombres
          propios, "clausura" trae hasta el Torneo Clausura de fútbol.
        </Ayuda>

        {creando && (
          <Formulario>
            <TechInput
              placeholder="Nombre del tema (ej: Clausuras a los locales de la calle Mitre)"
              value={nuevo.tema}
              onChange={(e) => setNuevo({ ...nuevo, tema: e.target.value })}
            />
            <TechTextarea
              rows={2}
              placeholder="Qué significa este tema y por qué importa (ayuda a la IA a no confundirse)"
              value={nuevo.descripcion}
              onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })}
            />
            <TechInput
              placeholder="Nombres propios del caso, separados por coma (ej: chouhy, moviport, casport)"
              value={nuevo.anclas}
              onChange={(e) => setNuevo({ ...nuevo, anclas: e.target.value })}
            />
            <TechTextarea
              rows={2}
              placeholder="Palabras del tema, separadas por coma (ej: clausura, faja, habilitación, multa)"
              value={nuevo.keywords}
              onChange={(e) => setNuevo({ ...nuevo, keywords: e.target.value })}
            />
            <Doble>
              <TechSelect
                value={nuevo.severidad_base}
                onChange={(e) => setNuevo({ ...nuevo, severidad_base: e.target.value })}
              >
                <option value="alta">Gravedad por defecto: alta</option>
                <option value="media">Gravedad por defecto: media</option>
                <option value="baja">Gravedad por defecto: baja</option>
              </TechSelect>
              <TechButton onClick={guardarNuevo} disabled={ocupado}>Guardar tema</TechButton>
              <TechButtonGhost onClick={() => { setCreando(false); setNuevo(VACIO); }} disabled={ocupado}>
                Cancelar
              </TechButtonGhost>
            </Doble>
          </Formulario>
        )}

        {temas.length === 0 ? (
          <Empty>Todavía no hay temas vigilados. Sin temas, el radar está dormido.</Empty>
        ) : (
          temas.map((t) => (
            <Fila key={t.id} style={{ opacity: t.activo ? 1 : 0.5 }}>
              <Principal>
                <Nombre>{t.tema}</Nombre>
                {t.descripcion && <Detalle>{t.descripcion}</Detalle>}
                <Palabras>
                  {(t.anclas || []).map((k) => (
                    <Palabra key={`a-${k}`} $ancla>{k}</Palabra>
                  ))}
                  {(t.keywords || []).filter((k) => !(t.anclas || []).includes(k))
                    .map((k) => <Palabra key={k}>{k}</Palabra>)}
                </Palabras>
                {!(t.anclas || []).length && (
                  <Sub>Sin nombres propios: este tema puede traer notas que no son del caso.</Sub>
                )}
              </Principal>
              <TechChip tone={tonoPorSeveridad(t.severidad_base)}>{t.severidad_base}</TechChip>
              {t.abiertas > 0 && (
                <TechChip tone="danger">
                  {t.abiertas} {t.abiertas === 1 ? 'situación' : 'situaciones'}
                </TechChip>
              )}
              <Botones>
                <TechButtonGhost onClick={() => alternarTema(t)} disabled={ocupado}>
                  {t.activo ? <><FaEyeSlash /> Pausar</> : <><FaEye /> Reanudar</>}
                </TechButtonGhost>
                <TechButtonDanger onClick={() => eliminarTema(t)} disabled={ocupado}>
                  <FaTrash />
                </TechButtonDanger>
              </Botones>
            </Fila>
          ))
        )}
      </TechPanel>

      {/* ---------- Historial ---------- */}
      {historial.length > 0 && (
        <TechPanel>
          <PanelHeader>
            <PanelTitle>Situaciones ya cerradas</PanelTitle>
          </PanelHeader>
          {historial.map((c) => (
            <Fila key={c.id}>
              <Principal>
                <Nombre style={{ color: 'var(--text-secondary)' }}>{c.titulo}</Nombre>
                <Detalle>
                  {ETIQUETA_ESTADO[c.estado] || c.estado}
                  {c.resolved_at && ` el ${new Date(c.resolved_at).toLocaleDateString('es-AR')}`}
                  {' · '}{c.senales} {c.senales === 1 ? 'señal' : 'señales'}
                </Detalle>
              </Principal>
              <Link to={`/crisis/${c.id}`}>
                <TechButtonGhost as="span"><FaFolderOpen /> Ver qué pasó</TechButtonGhost>
              </Link>
            </Fila>
          ))}
        </TechPanel>
      )}
    </TechShell>
  );
}
